import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import InitialLanding from './pages/InitialLanding';
import MainAppShell from './pages/MainAppShell';
import { ToastProvider } from './context/ToastContext';
import { resetCsrfToken } from './utils/csrf';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthCheckComplete, setIsAuthCheckComplete] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.ok && data.authenticated && data.user) {
          setCurrentUser(data.user);
          setIsAuthenticated(true);
        }
      } catch (_err) {
        // Keep app in logged-out state if session check fails.
      } finally {
        if (!cancelled) {
          setIsAuthCheckComplete(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch (_err) {
      // Even if network fails, clear local auth state.
    }
    // Server cleared the session (and its CSRF token); drop the cached one so
    // the next state-changing request fetches a fresh token. Without this, a
    // re-login in the same SPA session would 403 on the first write.
    resetCsrfToken();
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  const refreshCurrentUser = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      const data = await res.json();
      if (res.ok && data.ok && data.authenticated && data.user) {
        setCurrentUser(data.user);
      }
    } catch (_err) {
      // Leave existing user state on failure.
    }
  }, []);

  const handleGoogleLogin = useCallback(async (credential) => {
    const res = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential }),
    });

    let data;
    try {
      data = await res.json();
    } catch (_parseErr) {
      throw new Error(
        res.ok
          ? 'Invalid response from server'
          : `Server error (${res.status}); is the backend running on port 5000?`,
      );
    }

    if (!res.ok || !data.ok) {
      throw new Error(data.error || `Request failed (${res.status})`);
    }

    setCurrentUser(data.user);
    setIsAuthenticated(true);
  }, []);

  if (!isAuthCheckComplete) {
    return null;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/add" replace />
          ) : (
            <div className="flique-auth min-vh-100 d-flex align-items-center justify-content-center text-light">
              <div className="auth-shell">
                <InitialLanding onGoogleLogin={handleGoogleLogin} />
              </div>
            </div>
          )
        }
      />
      <Route
        path="/*"
        element={
          !isAuthenticated ? (
            <Navigate to="/" replace state={{ from: location.pathname }} />
          ) : (
            <ToastProvider>
              <MainAppShell
                user={currentUser}
                onLogout={onLogout}
                onUserUpdate={refreshCurrentUser}
              />
            </ToastProvider>
          )
        }
      />
    </Routes>
  );
}

export default App;
